<template>
  <aside
    class="workspace-side-rail"
    :class="{
      'workspace-side-rail--expanded': expanded,
      'workspace-side-rail--scholar': isScholarRoute
    }"
    aria-label="翻译工具快捷导航"
  >
    <div class="workspace-side-rail__scroll">
      <section
        v-for="group in groups"
        :key="group.key"
        class="workspace-side-rail__group"
      >
        <h2 v-show="expanded">{{ group.title }}</h2>
        <button
          v-for="item in group.items"
          :key="item.key"
          type="button"
          class="workspace-side-rail__item"
          :class="{ active: isActive(item) }"
          :title="expanded ? null : item.label"
          :aria-label="item.label"
          :aria-current="isActive(item) ? 'page' : null"
          :data-track-text="`侧边栏${item.label}`"
          @click="navigate(item)"
        >
          <i :class="item.icon" aria-hidden="true" />
          <span v-show="expanded">{{ item.label }}</span>
        </button>
      </section>
    </div>
    <button
      type="button"
      class="workspace-side-rail__toggle"
      :class="{ 'workspace-side-rail__toggle--expanded': expanded }"
      :aria-label="expanded ? '收起侧边导航' : '展开侧边导航'"
      :aria-expanded="String(expanded)"
      :title="expanded ? '收起侧边导航' : '展开侧边导航'"
      data-track-text="展开收起侧边导航"
      @click="$emit('toggle')"
    >
      <i class="el-icon-shousuo" aria-hidden="true" />
      <span v-show="expanded">收起导航</span>
    </button>
  </aside>
</template>

<script>
import { createWorkspaceSideRailGroups } from '@/config/workspaceMenu'

export default {
  name: 'WorkspaceSideRail',
  props: {
    expanded: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      groups: createWorkspaceSideRailGroups()
    }
  },
  computed: {
    isScholarRoute () {
      return this.$route.path === '/scholar'
    }
  },
  methods: {
    isActive (item) {
      const pathMatches = item.matchPrefix
        ? this.$route.path === item.path || this.$route.path.startsWith(`${item.path}/`)
        : this.$route.path === item.path
      return pathMatches && (!item.type || this.$route.query.type === item.type)
    },
    navigate (item) {
      if (item.path === '/trans-pro') {
        window.location.assign('/trans-pro/index')
        return
      }
      const target = item.type
        ? { path: item.path, query: { type: item.type } }
        : { path: item.path }
      const currentType = this.$route.query && this.$route.query.type
      if (this.$route.path === item.path && currentType === item.type) return
      this.$router.push(target)
    }
  }
}
</script>

<style scoped>
.workspace-side-rail {
  position: fixed;
  z-index: 48;
  top: 64px;
  bottom: 64px;
  left: 0;
  width: 54px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  border-right: 1px solid var(--border);
  background: linear-gradient(180deg, var(--bg-card) 0%, var(--primary-alpha-05) 100%);
  transition: width 0.18s ease;
}

.workspace-side-rail--expanded {
  width: 200px;
  border-right-color: #dce4e7;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.workspace-side-rail__scroll {
  min-height: 0;
  flex: 1 1 auto;
  padding: 14px 0 8px;
  overflow-x: hidden;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none !important;
}

.workspace-side-rail__scroll::-webkit-scrollbar {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
}

.workspace-side-rail--expanded .workspace-side-rail__scroll {
  padding: 14px 14px 8px;
  -ms-overflow-style: auto;
  scrollbar-width: thin !important;
  scrollbar-color: rgba(16, 185, 129, 0.24) transparent !important;
}

.workspace-side-rail--expanded .workspace-side-rail__scroll::-webkit-scrollbar {
  width: 5px !important;
  height: 5px !important;
  display: block !important;
}

.workspace-side-rail--expanded .workspace-side-rail__scroll::-webkit-scrollbar-track {
  background: transparent;
}

.workspace-side-rail--expanded .workspace-side-rail__scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.24);
}

.workspace-side-rail--expanded .workspace-side-rail__group + .workspace-side-rail__group {
  position: relative;
  margin-top: 8px;
  padding-top: 7px;
}

.workspace-side-rail--expanded .workspace-side-rail__group + .workspace-side-rail__group::before {
  position: absolute;
  top: 0;
  right: 8px;
  left: 8px;
  height: 1px;
  background: #edf2f7;
  content: '';
}

.workspace-side-rail__group h2 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 8px 8px;
  color: var(--primary-dark);
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.04em;
}

.workspace-side-rail__group h2::before {
  width: 3px;
  height: 14px;
  flex: 0 0 3px;
  border-radius: 999px;
  background: var(--primary-gradient);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.22);
  content: '';
}

.workspace-side-rail__item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 42px;
  padding: 0;
  box-sizing: border-box;
  border: 0;
  background: transparent;
  color: var(--muted-foreground);
  cursor: pointer;
  white-space: nowrap;
  transition: width 0.18s ease, color 0.16s ease, border-color 0.16s ease, background-color 0.16s ease, box-shadow 0.16s ease, filter 0.16s ease;
}

.workspace-side-rail--expanded .workspace-side-rail__item {
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  margin: 0 0 4px;
  padding: 0 8px;
  color: #425466;
  border: 1px solid transparent;
  border-radius: 9px;
  text-align: left;
}

.workspace-side-rail__item i {
  width: 20px;
  flex: 0 0 20px;
  font-size: 17px;
  text-align: center;
}

.workspace-side-rail__item span {
  min-width: 0;
  margin-left: 11px;
  overflow: hidden;
  font-size: 14px;
  font-weight: 500;
  text-overflow: ellipsis;
}

.workspace-side-rail--expanded .workspace-side-rail__item i {
  width: 18px;
  flex-basis: 18px;
  color: #7b8da1;
  font-size: 16px;
}

.workspace-side-rail--expanded .workspace-side-rail__item span {
  flex: 1 1 auto;
  margin-left: 0;
  color: inherit;
  line-height: 40px;
  text-align: left;
}

.workspace-side-rail--expanded .workspace-side-rail__item::after {
  width: 5px;
  height: 5px;
  flex: 0 0 5px;
  margin-left: auto;
  opacity: 0;
  border-top: 1.5px solid currentColor;
  border-right: 1.5px solid currentColor;
  content: '';
  transform: rotate(45deg);
  transition: opacity 0.16s ease;
}

.workspace-side-rail__item:hover,
.workspace-side-rail__item:focus-visible {
  color: var(--primary);
  background: var(--primary-alpha-08);
}

.workspace-side-rail__item:focus-visible {
  outline: 2px solid var(--primary-alpha-30);
  outline-offset: -5px;
}

.workspace-side-rail__item.active {
  color: #ffffff;
  background: var(--primary);
  box-shadow: var(--shadow-primary);
}

.workspace-side-rail--expanded .workspace-side-rail__item:not(.active):hover,
.workspace-side-rail--expanded .workspace-side-rail__item:not(.active):focus-visible {
  color: var(--primary-dark);
  border-color: rgba(16, 185, 129, 0.2);
  background: rgba(16, 185, 129, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.workspace-side-rail--expanded .workspace-side-rail__item:not(.active):hover i,
.workspace-side-rail--expanded .workspace-side-rail__item:not(.active):focus-visible i {
  color: var(--primary);
}

.workspace-side-rail--expanded .workspace-side-rail__item:hover::after,
.workspace-side-rail--expanded .workspace-side-rail__item:focus-visible::after,
.workspace-side-rail--expanded .workspace-side-rail__item.active::after {
  opacity: 0.8;
}

.workspace-side-rail--expanded .workspace-side-rail__item:focus-visible {
  outline-offset: 1px;
}

.workspace-side-rail--expanded .workspace-side-rail__item.active {
  border-color: transparent;
  background: var(--primary-gradient);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  font-weight: 600;
}

.workspace-side-rail--expanded .workspace-side-rail__item.active i,
.workspace-side-rail--expanded .workspace-side-rail__item.active span {
  color: #ffffff;
  font-weight: 600;
}

.workspace-side-rail--expanded .workspace-side-rail__item.active:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

.workspace-side-rail--expanded .workspace-side-rail__item:active {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
  filter: brightness(0.96);
}

.workspace-side-rail__toggle {
  position: relative;
  width: 100%;
  height: 54px;
  flex: 0 0 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0;
  border: 0;
  border-top: 1px solid var(--border);
  background: transparent;
  color: var(--muted-foreground);
  cursor: pointer;
  transition: color 0.16s ease, background-color 0.16s ease;
}

.workspace-side-rail__toggle i {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 20px;
  font-size: 17px;
  transition: transform 0.18s ease;
}

.workspace-side-rail__toggle span {
  margin-left: 11px;
  font-size: 14px;
  font-weight: 500;
}

.workspace-side-rail__toggle--expanded {
  justify-content: flex-start;
  padding: 0 22px;
}

.workspace-side-rail__toggle--expanded i {
  transform: rotate(180deg);
}

.workspace-side-rail__toggle:hover,
.workspace-side-rail__toggle:focus-visible {
  color: var(--primary);
  background: var(--primary-alpha-08);
  outline: none;
}

.workspace-side-rail__toggle:focus-visible::after {
  position: absolute;
  inset: 5px;
  border: 2px solid var(--primary-alpha-30);
  border-radius: 7px;
  content: '';
}

@media (max-width: 768px) {
  .workspace-side-rail {
    display: none;
  }
}

@media (min-width: 769px) and (max-width: 900px) {
  .workspace-side-rail {
    bottom: 128px;
  }
}
</style>
